import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Clock, Users, Trash2, Megaphone, ImagePlus, Eye, X } from "lucide-react";
import { api } from "../../lib/api";

const TEMPLATES = [
  { key: "announcement", label: "Announcement" },
  { key: "product", label: "Product Spotlight" },
];

const emptyForm = {
  template: "announcement",
  subject: "",
  heading: "",
  body: "",
  promo_code: "",
  cta_url: "",
  image_url: "",
  product_id: "",
  send_mode: "now",
  scheduled_at: "",
};

function StatusPill({ s }) {
  const map = {
    sent: "bg-emerald-50 text-emerald-700",
    scheduled: "bg-amber-50 text-amber-700",
    sending: "bg-blue-50 text-blue-700",
    draft: "bg-gray-100 text-gray-600",
  };
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${map[s] || "bg-gray-100 text-gray-600"}`}>{s}</span>;
}

export default function CampaignsManager() {
  const [subs, setSubs] = useState({ count: 0, total: 0, subscribers: [] });
  const [campaigns, setCampaigns] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [showSubs, setShowSubs] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);

  const loadSubs = () => api.get("/admin/subscribers").then((r) => setSubs(r.data)).catch(() => {});
  const loadCampaigns = () => api.get("/admin/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  useEffect(() => {
    loadSubs();
    loadCampaigns();
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const buildPayload = () => {
    const payload = {
      template: form.template,
      subject: form.subject,
      heading: form.heading,
      body: form.body,
      promo_code: form.promo_code,
      cta_url: form.cta_url,
      image_url: form.image_url,
      send_mode: form.send_mode,
    };
    if (form.scheduled_at) payload.scheduled_at = new Date(form.scheduled_at).toISOString();
    const p = products.find((x) => x.id === form.product_id);
    if (p) payload.product = { name: p.name, price: p.price, image: p.image_url, url: `${window.location.origin}/product/${p.id}` };
    return payload;
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/campaign-images", fd);
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const preview = async () => {
    try {
      const { data } = await api.post("/admin/campaigns/preview", buildPayload());
      setPreviewHtml(data.html);
    } catch {
      toast.error("Could not build preview");
    }
  };

  const submit = async () => {
    if (!form.subject.trim()) { toast.error("Subject is required"); return; }
    if (form.send_mode === "schedule" && !form.scheduled_at) { toast.error("Pick a send date/time"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/campaigns", buildPayload());
      toast.success(data.status === "scheduled" ? "Campaign scheduled" : `Sending to ${subs.count} subscribers`);
      setForm(emptyForm);
      loadCampaigns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create campaign");
    } finally {
      setBusy(false);
    }
  };

  const removeSub = async (id) => {
    await api.delete(`/admin/subscribers/${id}`).catch(() => {});
    loadSubs();
  };

  return (
    <div className="space-y-6" data-testid="campaigns-manager">
      {/* Subscribers summary */}
      <div className="rounded-2xl border border-line bg-white p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-crimson" />
            <span className="font-bold text-ink" data-testid="subscriber-count">{subs.count} active subscribers</span>
            {subs.total > subs.count && <span className="text-sm text-muted-foreground">({subs.total - subs.count} unsubscribed)</span>}
          </div>
          <button onClick={() => setShowSubs((v) => !v)} data-testid="toggle-subscribers" className="text-sm font-bold text-ink hover:text-crimson">
            {showSubs ? "Hide list" : "View list"}
          </button>
        </div>
        {showSubs && (
          <ul className="mt-4 divide-y divide-line">
            {subs.subscribers.map((s) => (
              <li key={s._id} className="flex items-center justify-between py-2 text-sm">
                <span className={s.active ? "text-ink" : "text-muted-foreground line-through"}>{s.email}</span>
                <button onClick={() => removeSub(s._id)} className="text-muted-foreground hover:text-crimson"><Trash2 className="h-4 w-4" /></button>
              </li>
            ))}
            {subs.subscribers.length === 0 && <li className="py-2 text-sm text-muted-foreground">No subscribers yet.</li>}
          </ul>
        )}
      </div>

      {/* Compose */}
      <div className="rounded-2xl border border-line bg-white p-6">
        <div className="mb-4 flex items-center gap-2">
          <Megaphone className="h-5 w-5 text-crimson" />
          <h3 className="font-display text-lg font-bold text-ink">New campaign</h3>
        </div>

        <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Template</label>
        <div className="mt-2 mb-4 flex gap-2">
          {TEMPLATES.map((t) => (
            <button
              key={t.key}
              onClick={() => setForm((f) => ({ ...f, template: t.key }))}
              data-testid={`campaign-template-${t.key}`}
              className={`rounded-full border px-4 py-2 text-sm font-bold transition-colors ${form.template === t.key ? "border-crimson bg-crimson text-white" : "border-line bg-white text-ink hover:border-ink"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="grid gap-4">
          <Field label="Subject line" v={form.subject} on={set("subject")} testId="campaign-subject" placeholder="Fall sale — save this weekend" />
          <Field label="Heading" v={form.heading} on={set("heading")} testId="campaign-heading" placeholder="Big headline shown in the email" />
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Message</label>
            <textarea value={form.body} onChange={set("body")} rows={4} data-testid="campaign-body"
              placeholder="Write your announcement…"
              className="mt-2 w-full rounded-xl border border-line bg-alt p-3 text-sm text-ink outline-none focus:border-crimson" />
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Banner image (optional)</label>
            <div className="mt-2 flex items-center gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-line bg-white px-4 py-2 text-sm font-bold text-ink hover:border-crimson">
                <ImagePlus className="h-4 w-4" />
                {uploading ? "Uploading…" : form.image_url ? "Replace image" : "Upload image"}
                <input type="file" accept="image/*" onChange={uploadImage} data-testid="campaign-image-upload" className="hidden" />
              </label>
              {form.image_url && (
                <div className="flex items-center gap-2">
                  <img src={form.image_url} alt="banner" className="h-12 w-20 rounded-lg object-cover" data-testid="campaign-image-preview" />
                  <button type="button" onClick={() => setForm((f) => ({ ...f, image_url: "" }))} className="text-muted-foreground hover:text-crimson"><Trash2 className="h-4 w-4" /></button>
                </div>
              )}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Promo code (optional)" v={form.promo_code} on={set("promo_code")} testId="campaign-promo" placeholder="e.g. SAVE50" />
            {form.template === "product" ? (
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Feature a product</label>
                <select value={form.product_id} onChange={set("product_id")} data-testid="campaign-product"
                  className="mt-2 block w-full rounded-xl border border-line bg-white px-3 py-2.5 text-sm text-ink outline-none focus:border-crimson">
                  <option value="">Select a product…</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name} — ${p.price}</option>)}
                </select>
              </div>
            ) : (
              <Field label="Button link (optional)" v={form.cta_url} on={set("cta_url")} testId="campaign-cta" placeholder="https://adoptaphone.com/products" />
            )}
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">When to send</label>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <button onClick={() => setForm((f) => ({ ...f, send_mode: "now" }))} data-testid="campaign-send-now"
                className={`rounded-full border px-4 py-2 text-sm font-bold ${form.send_mode === "now" ? "border-crimson bg-crimson text-white" : "border-line bg-white text-ink hover:border-ink"}`}>
                Send now
              </button>
              <button onClick={() => setForm((f) => ({ ...f, send_mode: "schedule" }))} data-testid="campaign-schedule"
                className={`rounded-full border px-4 py-2 text-sm font-bold ${form.send_mode === "schedule" ? "border-crimson bg-crimson text-white" : "border-line bg-white text-ink hover:border-ink"}`}>
                Schedule
              </button>
              {form.send_mode === "schedule" && (
                <input type="datetime-local" value={form.scheduled_at} onChange={set("scheduled_at")} data-testid="campaign-datetime"
                  className="rounded-xl border border-line px-3 py-2 text-sm outline-none focus:border-crimson" />
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button onClick={preview} data-testid="campaign-preview-btn"
              className="inline-flex items-center gap-2 rounded-full border border-ink px-6 py-3 text-sm font-bold text-ink transition-colors hover:bg-ink hover:text-white">
              <Eye className="h-4 w-4" /> Preview email
            </button>
            <button onClick={submit} disabled={busy} data-testid="campaign-submit"
              className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-crimson disabled:opacity-40">
              {form.send_mode === "schedule" ? <Clock className="h-4 w-4" /> : <Send className="h-4 w-4" />}
              {busy ? "Working…" : form.send_mode === "schedule" ? "Schedule campaign" : `Send to ${subs.count} subscribers`}
            </button>
          </div>
        </div>
      </div>

      {/* History */}
      <div className="rounded-2xl border border-line bg-white p-5">
        <h3 className="mb-3 font-bold text-ink">Campaign history</h3>
        <div className="space-y-2" data-testid="campaign-history">
          {campaigns.length === 0 && <p className="text-sm text-muted-foreground">No campaigns yet.</p>}
          {campaigns.map((c) => (
            <div key={c._id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-line px-4 py-3">
              <div>
                <div className="font-bold text-ink">{c.subject}</div>
                <div className="text-xs text-muted-foreground">
                  {c.status === "sent" && `Sent to ${c.recipients_count} · ${new Date(c.sent_at).toLocaleString()}`}
                  {c.status === "scheduled" && `Scheduled for ${new Date(c.scheduled_at).toLocaleString()}`}
                  {c.status === "sending" && "Sending…"}
                </div>
              </div>
              <StatusPill s={c.status} />
            </div>
          ))}
        </div>
      </div>
      {previewHtml !== null && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" data-testid="campaign-preview-modal">
          <div className="flex max-h-[85vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white">
            <div className="flex items-center justify-between border-b border-line px-5 py-3">
              <span className="font-bold text-ink">Email preview</span>
              <button onClick={() => setPreviewHtml(null)} data-testid="campaign-preview-close" className="text-muted-foreground hover:text-crimson"><X className="h-5 w-5" /></button>
            </div>
            <iframe title="email-preview" srcDoc={previewHtml} className="h-[70vh] w-full border-0" />
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, v, on, testId, placeholder }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}</label>
      <input value={v} onChange={on} data-testid={testId} placeholder={placeholder}
        className="mt-2 w-full rounded-xl border border-line px-3 py-2.5 text-sm text-ink outline-none focus:border-crimson" />
    </div>
  );
}
